import { Typography } from '@/components/atoms';
import { PlaceholderImage } from '@/components/atoms/PlaceholderImage';
import { ImageCarousel } from '@/components/molecules/ImageCarousel';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { trackGuestProjectCarouselManualChange } from '@/core/services/trackDynatraceCatalog';
import React, { useCallback, useMemo } from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type ImageSourcePropType,
} from 'react-native';
import { GUEST_CARD_MAX_WIDTH } from '../constants/guestLayout';
import type { GuestPropertyCardModel } from '../types/guest.types';
import { GuestUnitTypeTagsRow } from './GuestUnitTypeTagsRow';

export interface GuestPropertyCardProps {
  readonly property: GuestPropertyCardModel;
  readonly onPress?: () => void;
  /**
   * When true, the card fills its parent's width instead of capping at the
   * Figma listing-grid max width (442.67). Use in contexts with their own
   * width constraint (e.g. details "explore" sidebar) so the card can grow
   * wider than the listing grid column.
   */
  readonly fullWidth?: boolean;
  /** Turns off web carousel auto-scroll. Used by the details explore sidebar. */
  readonly disableAutoScroll?: boolean;
}

const CARD_CAROUSEL_HEIGHT = 230;
const CARD_HEIGHT = 344;

/**
 * Guest project/property card (web) — shared by list grid and details explore.
 * Uses shared ImageCarousel; card width is 100% of parent.
 * Unit-type chips hug label + padding; extras are hidden (full list on details).
 */
export function GuestPropertyCard({
  property,
  onPress,
  fullWidth = false,
  disableAutoScroll = false,
}: Readonly<GuestPropertyCardProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  const carouselImages = useMemo<ImageSourcePropType[]>(
    () => property.images.map((img) => ({ uri: img.uri })),
    [property.images],
  );
  const handleManualCarouselChange = useCallback(
    (imageIndex: number) => {
      trackGuestProjectCarouselManualChange(property.id, imageIndex);
    },
    [property.id],
  );
  const carouselOnPressProps = onPress ? { onPress } : {};
  const carouselAutoScrollProps = disableAutoScroll
    ? { disableAutoScroll: true as const }
    : {};
  const unitsBadgeSide = isRTL ? 'right' : 'left';

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole="button"
      accessibilityLabel={property.title}
      style={({ pressed }) => [
        styles.container,
        fullWidth ? styles.containerFullWidth : styles.containerInGrid,
        {
          backgroundColor: colors.backgroundLevel1,
          opacity: onPress && pressed ? 0.94 : 1,
        },
      ]}
    >
      <View
        style={[
          styles.imageWrap,
          { backgroundColor: colors.backgroundLevel2 },
        ]}
      >
        {carouselImages.length > 0 ? (
          <ImageCarousel
            images={carouselImages}
            height={CARD_CAROUSEL_HEIGHT}
            paginationVariant="listing"
            onManualIndexChange={handleManualCarouselChange}
            autoScrollSeed={property.id}
            {...carouselOnPressProps}
            {...carouselAutoScrollProps}
          />
        ) : (
          <View
            style={[
              styles.imagePlaceholder,
              { backgroundColor: colors.backgroundLevel2 },
            ]}
          >
            <PlaceholderImage />
          </View>
        )}

        {property.unitsBadge ? (
          <View
            style={[
              styles.unitsBadge,
              {
                backgroundColor: colors.unitsBadgeBg,
                [unitsBadgeSide]: 12,
              },
            ]}
            pointerEvents="none"
          >
            <Typography
              variant="caption"
              color="primary"
              style={styles.unitsBadgeText}
            >
              {property.unitsBadge}
            </Typography>
          </View>
        ) : null}
      </View>

      <View style={styles.content}>
        <View style={styles.textGroup}>
          <Typography
            variant="title3"
            color="primary"
            numberOfLines={1}
            style={styles.titleText}
          >
            {property.title}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            numberOfLines={2}
            style={styles.locationText}
          >
            {property.location}
          </Typography>
        </View>
        <GuestUnitTypeTagsRow
          tags={property.typeTags}
          tagBackgroundColor={colors.propertyTagBg}
        />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  /** Figma: radius 12, padding 8. Gap between carousel and title/content. */
  container: {
    borderRadius: 12,
    borderWidth: 0,
    overflow: 'hidden',
    width: '100%',
    maxWidth: GUEST_CARD_MAX_WIDTH,
    minWidth: 0,
    minHeight: CARD_HEIGHT,
    padding: 8,
    gap: 16,
    flexDirection: 'column',
  },
  /** Listing grid — stretch to tallest sibling in the row. */
  containerInGrid: {
    flex: 1,
  },
  /**
   * Details explore stack/sidebar: fill width, no flex stretch so the card
   * keeps a stable height with single-row tags.
   */
  containerFullWidth: {
    maxWidth: '100%',
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  imageWrap: {
    width: '100%',
    maxWidth: '100%',
    height: CARD_CAROUSEL_HEIGHT,
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  /** Same empty-media treatment as properties-web ListingCard. */
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unitsBadge: {
    position: 'absolute',
    top: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    zIndex: 2,
    maxWidth: '80%',
  },
  unitsBadgeText: {
    fontFamily: 'Matter-SemiBold',
    lineHeight: 22,
    letterSpacing: 0,
  },
  /**
   * Title/location at top; unit types on the bottom edge (single row).
   */
  content: {
    width: '100%',
    minWidth: 0,
    gap: 12,
    flexGrow: 1,
    flexShrink: 0,
    justifyContent: 'space-between',
  },
  textGroup: {
    gap: 6,
    width: '100%',
    minWidth: 0,
  },
  titleText: {
    letterSpacing: 0,
  },
  locationText: {
    letterSpacing: 0,
    /** Caption is 12/12 by default — too tight; Matter descenders (g, y) clip. */
    lineHeight: 16,
  },
});
